import { coreGraphql } from "../api/coreClient";
import { websiteGraphqlRequest } from "./websiteGraphql";
import {
    CORE_MUTATION_ACCEPT_FRIEND_REQUEST,
    CORE_MUTATION_BLOCK_USER,
    CORE_MUTATION_CANCEL_FRIEND_REQUEST,
    CORE_MUTATION_CANCEL_FRIEND_REQUEST_TO_USER,
    CORE_MUTATION_FOLLOW_USER,
    CORE_MUTATION_REJECT_FRIEND_REQUEST,
    CORE_MUTATION_REMOVE_FRIEND,
    CORE_MUTATION_SEND_FRIEND_REQUEST,
    CORE_MUTATION_UNBLOCK_USER,
    CORE_MUTATION_UNFOLLOW_USER,
    CORE_MUTATION_VOUCH_FOR_USER,
} from "../graphql/mutations/socialMutations";
import { relayGlobalIdToDbId } from "../helpers/graphql";

const CORE_QUERY_PENDING_INCOMING_FRIEND_REQUEST_ID = `
  query CorePendingIncomingFriendRequestId($username: String!) {
    pendingIncomingFriendRequest(username: $username) {
      id
    }
  }
`;

/**
 * Core GraphQL — follow (viewer follows target by username).
 * @param {string|null|undefined} token
 * @param {string} username
 * @returns {Promise<{ ok?: boolean, error?: string|null }|undefined>}
 */
export async function coreFollowUserMutation(token, username) {
    const data = await coreGraphql(
        CORE_MUTATION_FOLLOW_USER,
        { username },
        token,
    );
    return data?.followUser;
}

/**
 * Core GraphQL — unfollow.
 * @param {string|null|undefined} token
 * @param {string} username
 * @returns {Promise<{ ok?: boolean, error?: string|null, changed?: boolean }|undefined>}
 */
export async function coreUnfollowUserMutation(token, username) {
    const data = await coreGraphql(
        CORE_MUTATION_UNFOLLOW_USER,
        { username },
        token,
    );
    return data?.unfollowUser;
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function followUserApi(token, username) {
    return coreGraphql(CORE_MUTATION_FOLLOW_USER, { username }, token);
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function unfollowUserApi(token, username) {
    return coreGraphql(CORE_MUTATION_UNFOLLOW_USER, { username }, token);
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function sendFriendRequestApi(token, username) {
    return websiteGraphqlRequest(
        CORE_MUTATION_SEND_FRIEND_REQUEST,
        { username },
        token,
    );
}

/**
 * @param {string|null|undefined} token
 * @param {number} requestId database `FriendRequest` pk
 */
export async function acceptFriendRequestApi(token, requestId) {
    return websiteGraphqlRequest(
        CORE_MUTATION_ACCEPT_FRIEND_REQUEST,
        { requestId },
        token,
    );
}

/**
 * @param {string|null|undefined} token
 * @param {number} requestId database `FriendRequest` pk
 */
export async function rejectFriendRequestApi(token, requestId) {
    return websiteGraphqlRequest(
        CORE_MUTATION_REJECT_FRIEND_REQUEST,
        { requestId },
        token,
    );
}

/**
 * @param {string|null|undefined} token
 * @param {number} requestId database `FriendRequest` pk
 */
export async function cancelFriendRequestApi(token, requestId) {
    return websiteGraphqlRequest(
        CORE_MUTATION_CANCEL_FRIEND_REQUEST,
        { requestId },
        token,
    );
}

/**
 * Core GraphQL — cancel outgoing pending friend request by target username.
 * @param {string|null|undefined} token
 * @param {string} username
 * @returns {Promise<{ ok?: boolean, error?: string|null, friendRequest?: unknown }|undefined>}
 */
export async function coreCancelFriendRequestToUserMutation(token, username) {
    const data = await coreGraphql(
        CORE_MUTATION_CANCEL_FRIEND_REQUEST_TO_USER,
        { username },
        token,
    );
    return data?.cancelFriendRequestToUser;
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function removeFriendApi(token, username) {
    return websiteGraphqlRequest(
        CORE_MUTATION_REMOVE_FRIEND,
        { username },
        token,
    );
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function blockUserApi(token, username) {
    return websiteGraphqlRequest(
        CORE_MUTATION_BLOCK_USER,
        { username },
        token,
    );
}

/**
 * @param {string|null|undefined} token
 * @param {string} username
 */
export async function unblockUserApi(token, username) {
    return websiteGraphqlRequest(
        CORE_MUTATION_UNBLOCK_USER,
        { username },
        token,
    );
}

/**
 * Accept or reject the pending request `username` sent to the viewer — for screens that only
 * know the sender (received list, legacy notifications), not the request pk.
 * @returns {Promise<{ ok: boolean, error?: string|null }>}
 */
export async function respondToFriendRequestFromUser(token, username, accept) {
    const data = await coreGraphql(
        CORE_QUERY_PENDING_INCOMING_FRIEND_REQUEST_ID,
        { username },
        token,
    );
    const requestId = relayGlobalIdToDbId(data?.pendingIncomingFriendRequest?.id);
    if (!requestId) {
        return { ok: false, error: "Friend request not found." };
    }
    const raw = accept
        ? await acceptFriendRequestApi(token, requestId)
        : await rejectFriendRequestApi(token, requestId);
    const payload = accept ? raw?.acceptFriendRequest : raw?.rejectFriendRequest;
    return { ok: !!payload?.ok, error: payload?.error || null };
}

/**
 * A friend confirms `username`'s account; enough confirmations grant the verified badge.
 * @returns {Promise<{ ok: boolean, error: string|null, vouchCount: number, isVerified: boolean }>}
 */
export async function vouchForUserApi(token, username) {
    const data = await coreGraphql(CORE_MUTATION_VOUCH_FOR_USER, { username }, token);
    const payload = data?.vouchForUser;
    return {
        ok: !!payload?.ok,
        error: payload?.error || null,
        vouchCount: payload?.vouchCount ?? 0,
        isVerified: !!payload?.isVerified,
    };
}
